import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, ArrowRight, Phone, AlertTriangle } from 'lucide-react';

const PHONE = "403-615-8770";

const blogPosts = {
  'tank-vs-tankless-water-heater': {
    title: 'Tank vs Tankless Water Heater: Which Is Right For You?',
    excerpt: 'Comparing traditional tank and tankless water heaters for Calgary homes.',
    image: '/images/dnb/blog-tank-vs-tankless.jpg',
    imageAlt: 'Tank vs tankless water heater comparison for Calgary homes by DNB',
    category: 'Plumbing',
    readTime: '6 min read',
    date: 'January 15, 2026',
    content: [
      { type: 'h2', text: 'Making the Right Choice for Your Home\'s Hot Water' },
      { type: 'p', text: 'When it\'s time to replace your water heater, you\'ll face an important decision: should you go with a traditional tank system or upgrade to a tankless unit? Both have their advantages, and the best choice depends on your specific needs and household size.' },
      { type: 'h3', text: 'Traditional Tank Water Heaters' },
      { type: 'p', text: 'Tank water heaters store and continuously heat a reservoir of water (typically 40-80 gallons). They\'ve been the standard for decades and remain a reliable choice for many Calgary homeowners.' },
      { type: 'h4', text: 'Advantages:' },
      { type: 'list', items: ['Lower upfront cost ($800-$1,500 installed)', 'Simple, proven technology', 'Works during power outages (gas models)', 'Easy service and repairs', 'Lower installation complexity'] },
      { type: 'h4', text: 'Disadvantages:' },
      { type: 'list', items: ['Higher operating costs due to standby heat loss', 'Limited hot water supply - can run out during heavy use', 'Takes up significant floor space', 'Shorter lifespan (10-15 years)', 'Risk of tank leaks causing water damage'] },
      { type: 'h3', text: 'Tankless Water Heaters' },
      { type: 'p', text: 'Tankless (on-demand) water heaters heat water only when you need it, providing endless hot water without a storage tank. They\'re becoming increasingly popular in Calgary homes.' },
      { type: 'h4', text: 'Advantages:' },
      { type: 'list', items: ['Endless hot water on demand', '24-34% more energy efficient', 'Compact, wall-mounted design saves space', 'Longer lifespan (20+ years)', 'No risk of tank leaks or flooding'] },
      { type: 'h4', text: 'Disadvantages:' },
      { type: 'list', items: ['Higher upfront cost ($2,500-$4,500 installed)', 'May require electrical or gas line upgrades', 'Flow rate limitations with multiple simultaneous uses', 'Annual service recommended for Calgary\'s hard water'] },
      { type: 'h3', text: 'Which Should You Choose?' },
      { type: 'p', text: 'Consider tankless if you have a smaller household, want long-term energy savings, have limited space, or plan to stay in your home long-term. Consider a tank system if you have a larger family with high simultaneous hot water demand, have budget constraints, or prefer a simpler installation.' },
      { type: 'p', text: 'Contact DNB for a personalized recommendation based on your home\'s specific needs and usage patterns.' }
    ]
  },
  'should-you-replace-polyb': {
    title: 'Should You Replace Poly-B Plumbing? Insurance & Safety Concerns',
    excerpt: 'Understanding the risks of Poly-B piping and why many Calgary homeowners are choosing replacement.',
    image: '/images/dnb/blog-polyb.jpg',
    imageAlt: 'Calgary Poly-B replacement with new PEX piping by DNB',
    category: 'Plumbing',
    readTime: '7 min read',
    date: 'January 12, 2026',
    content: [
      { type: 'h2', text: 'The Hidden Risk in Thousands of Calgary Homes' },
      { type: 'p', text: 'If your Calgary home was built between 1978 and 1995, there\'s a significant chance it has Poly-B (polybutylene) plumbing. While once considered a cost-effective alternative to copper, Poly-B has proven problematic over time and is no longer used in new construction. Understanding the risks can help you make an informed decision about your home\'s plumbing.' },
      { type: 'h3', text: 'What is Poly-B Piping?' },
      { type: 'p', text: 'Polybutylene is a gray plastic pipe that was widely used in residential plumbing from the late 1970s through the mid-1990s. It was popular because it was inexpensive and easy to install. Millions of homes across North America were built with Poly-B plumbing, including a large number in Calgary and surrounding areas.' },
      { type: 'h3', text: 'Why Poly-B Fails' },
      { type: 'p', text: 'Poly-B pipes deteriorate from the inside out when exposed to chlorine and other oxidants commonly found in municipal water supplies. This degradation is invisible from the outside, making failures sudden and often catastrophic. The pipes become brittle and prone to cracking, leading to leaks that can cause extensive water damage.' },
      { type: 'alert', text: 'Warning: Poly-B failures are often sudden and can cause significant water damage to your home, including flooring, drywall, and personal belongings.' },
      { type: 'h3', text: 'Signs of Poly-B Problems' },
      { type: 'list', items: ['Visible discoloration or flaking on plastic fittings', 'Unexplained leaks, especially at connections and fittings', 'Reduced water pressure throughout the home', 'Water damage without an obvious source', 'Moisture, staining, or bubbling on walls and ceilings', 'Musty odors indicating hidden moisture'] },
      { type: 'h3', text: 'Insurance Implications - A Growing Concern' },
      { type: 'p', text: 'This is where Poly-B becomes more than just a plumbing concern—it\'s a financial one. Many insurance companies have changed their policies regarding homes with Poly-B plumbing:' },
      { type: 'list', items: ['Some insurers charge significantly higher premiums (20-50% more) for homes with Poly-B', 'Some require a plumbing inspection before providing coverage', 'A growing number of insurers refuse to cover homes with Poly-B at all', 'Claims related to Poly-B failures may be denied or result in policy cancellation', 'When selling your home, Poly-B can complicate the buyer\'s ability to get insurance'] },
      { type: 'p', text: 'Before your next insurance renewal, it\'s worth checking with your provider about their Poly-B policy. Replacement may actually save you money on premiums over time.' },
      { type: 'h3', text: 'The Replacement Process' },
      { type: 'p', text: 'Modern Poly-B replacement typically uses PEX (cross-linked polyethylene) piping, which is flexible, durable, and resistant to scale and chlorine. PEX has been the industry standard for over 20 years and has an excellent track record.' },
      { type: 'p', text: 'A professional replacement for an average Calgary home can often be completed in 1-3 days depending on home size and complexity. We work to minimize disruption to your daily routine, and most homeowners can remain in the home during the work.' },
      { type: 'h3', text: 'Cost vs. Risk' },
      { type: 'p', text: 'While Poly-B replacement is an investment, consider the potential costs of not replacing: water damage repairs (often $10,000-$50,000+), mold remediation, lost personal items, higher insurance premiums, and the stress of dealing with a plumbing emergency.' },
      { type: 'p', text: 'DNB specializes in Poly-B replacement for Calgary homes. Contact us for a free assessment and honest recommendation about your home\'s plumbing.' }
    ]
  },
  'furnace-heat-exchanger-dangers': {
    title: 'Cracked Heat Exchanger: The Hidden Danger in Your Furnace',
    excerpt: 'Understanding the serious safety risks of a cracked heat exchanger and why regular inspections matter.',
    image: '/images/dnb/blog-heat-exchanger.jpg',
    imageAlt: 'Furnace heat exchanger inspection in a Calgary home by DNB',
    category: 'Heating',
    readTime: '6 min read',
    date: 'January 10, 2026',
    content: [
      { type: 'h2', text: 'The Most Dangerous Furnace Problem You Can\'t See' },
      { type: 'p', text: 'Your furnace\'s heat exchanger is one of its most critical safety components, yet most homeowners have never heard of it. A cracked heat exchanger can pose serious health risks to your family, making regular furnace inspections essential for every Calgary home.' },
      { type: 'h3', text: 'What is a Heat Exchanger?' },
      { type: 'p', text: 'The heat exchanger is a metal chamber inside your furnace where combustion gases are contained while heating the air that circulates through your home. It acts as a barrier between the combustion process (and its byproducts) and the air you breathe.' },
      { type: 'alert', text: 'DANGER: A cracked heat exchanger can allow carbon monoxide and other combustion gases to enter your home\'s air supply. Carbon monoxide is colorless and odorless, making it nearly impossible to detect without proper equipment.' },
      { type: 'h3', text: 'Carbon Monoxide: The Silent Killer' },
      { type: 'p', text: 'Carbon monoxide (CO) poisoning is responsible for hundreds of deaths annually in North America. Symptoms often mimic the flu, making it difficult to identify:' },
      { type: 'list', items: ['Headaches (often the first symptom)', 'Dizziness and confusion', 'Nausea and vomiting', 'Fatigue and weakness', 'Shortness of breath', 'In severe cases: loss of consciousness and death'] },
      { type: 'p', text: 'If multiple family members experience these symptoms simultaneously, especially during heating season, evacuate immediately and call 911. Do not re-enter until the home has been inspected.' },
      { type: 'h3', text: 'What Causes Heat Exchanger Cracks?' },
      { type: 'list', items: ['Age and normal wear (most common in furnaces 15+ years old)', 'Overheating due to restricted airflow (dirty filters are a major cause)', 'Improper sizing - furnace too large or small for the home', 'Poor installation or care', 'Metal fatigue from repeated thermal expansion cycles'] },
      { type: 'h3', text: 'Warning Signs of Heat Exchanger Problems' },
      { type: 'list', items: ['Yellow or flickering furnace flame (should be steady blue)', 'Soot buildup inside the furnace', 'Strong, unusual odors when furnace runs', 'Visible cracks or corrosion on furnace components', 'CO detector alarms (install them if you haven\'t!)', 'Family members experiencing flu-like symptoms during heating season'] },
      { type: 'h3', text: 'Prevention: Annual Furnace Inspections' },
      { type: 'p', text: 'The best way to protect your family is with annual furnace inspections by a qualified technician. During an inspection, we check the heat exchanger for cracks, test combustion gases, verify proper venting, and ensure all safety systems are functioning correctly.' },
      { type: 'p', text: 'Calgary\'s extreme temperature swings put extra stress on furnaces, making annual service even more important. A furnace that worked fine last year may have developed problems over the summer.' },
      { type: 'h3', text: 'What to Do If You Suspect a Problem' },
      { type: 'list', items: ['Turn off the furnace immediately', 'Open windows to ventilate', 'Leave the home if anyone is experiencing symptoms', 'Call a professional for an inspection', 'Do not attempt to repair yourself - this is a safety issue'] },
      { type: 'p', text: 'DNB provides comprehensive furnace inspections for Calgary homes. Protect your family with regular service - contact us to schedule your inspection today.' }
    ]
  },
  'garage-heater-installation-guide': {
    title: 'Garage Heater Installation Guide for Calgary Homeowners',
    excerpt: 'Everything you need to know about installing a garage heater in Calgary\'s climate.',
    image: '/images/dnb/blog-garage-heater.jpg',
    imageAlt: 'Natural gas garage heater installed in a Calgary garage by DNB',
    category: 'Heating',
    readTime: '6 min read',
    date: 'January 8, 2026',
    content: [
      { type: 'h2', text: 'Why Calgary Homeowners Are Heating Their Garages' },
      { type: 'p', text: 'With Calgary\'s harsh winters regularly dipping to -20°C and below, a garage heater isn\'t just a luxury—it\'s a practical investment that offers numerous benefits beyond simple comfort.' },
      { type: 'h3', text: 'Benefits of a Heated Garage' },
      { type: 'list', items: ['Protect vehicles from cold-weather wear and easier starts', 'Create a usable year-round workshop or hobby space', 'Prevent frozen pipes in or near the garage', 'Reduce strain on your home\'s heating system', 'Keep stored items protected from extreme cold'] },
      { type: 'h3', text: 'Types of Garage Heaters' },
      { type: 'h4', text: 'Natural Gas Heaters' },
      { type: 'p', text: 'The most popular choice for Calgary garages. Natural gas heaters provide powerful, cost-effective heat. They require a gas line installation but offer the lowest operating costs.' },
      { type: 'h4', text: 'Propane Heaters' },
      { type: 'p', text: 'A good option if natural gas isn\'t available. Propane heaters work similarly to natural gas units but require propane tank storage and refilling.' },
      { type: 'h3', text: 'What\'s Involved in Installation' },
      { type: 'p', text: 'A professional garage heater installation typically includes:' },
      { type: 'list', items: ['Assessment of your garage size and insulation', 'Sizing the heater for adequate warmth', 'Gas line installation or extension', 'Proper venting to the exterior', 'Electrical connections for thermostat and controls', 'Permits and inspections as required'] },
      { type: 'h3', text: 'Cost Considerations' },
      { type: 'p', text: 'Budget for both the heater unit and installation. A complete installation typically ranges from $1,500-$3,500 depending on heater size, gas line requirements, and your garage\'s configuration.' },
      { type: 'p', text: 'Contact DNB for a personalized quote on garage heater installation.' }
    ]
  },
  'signs-furnace-failing': {
    title: 'Signs Your Furnace Is Failing: When to Repair or Replace',
    excerpt: 'Don\'t wait for a complete breakdown. Learn the warning signs that your furnace needs attention.',
    image: '/images/dnb/blog-furnace-failing.jpg',
    imageAlt: 'Calgary furnace diagnosis and repair by DNB',
    category: 'Heating',
    readTime: '5 min read',
    date: 'January 5, 2026',
    content: [
      { type: 'h2', text: 'Is Your Furnace Telling You It\'s Time for Attention?' },
      { type: 'p', text: 'Your furnace is one of the most important appliances in your Calgary home, especially during our harsh winters. Recognizing the warning signs of a failing furnace can help you avoid an unexpected breakdown on the coldest night of the year.' },
      { type: 'h3', text: 'Warning Signs to Watch For' },
      { type: 'h4', text: '1. Age of Your Furnace' },
      { type: 'p', text: 'Most furnaces have a lifespan of 15-20 years. If your furnace is approaching or exceeding this age, it\'s time to start planning for a replacement, even if it\'s still running.' },
      { type: 'h4', text: '2. Rising Energy Bills' },
      { type: 'p', text: 'If your heating bills have been steadily increasing without a corresponding increase in usage, your furnace is likely losing efficiency.' },
      { type: 'h4', text: '3. Frequent Repairs' },
      { type: 'p', text: 'If you\'re calling for repairs more than once a year, those costs add up quickly. A good rule: if repair cost exceeds 50% of a new furnace cost, replacement is better.' },
      { type: 'h4', text: '4. Uneven Heating' },
      { type: 'p', text: 'Some rooms too hot while others are too cold? This could indicate your furnace is struggling to distribute heat evenly throughout your home.' },
      { type: 'h4', text: '5. Strange Noises' },
      { type: 'p', text: 'Banging, popping, rattling, or squealing noises often indicate mechanical problems. Persistent noises in an older furnace suggest worn components.' },
      { type: 'h4', text: '6. Yellow Pilot Light' },
      { type: 'p', text: 'A healthy furnace flame should be blue. A yellow or flickering flame could indicate carbon monoxide issues - a serious safety concern requiring immediate attention.' },
      { type: 'h3', text: 'Repair or Replace?' },
      { type: 'p', text: 'Contact DNB for an honest assessment. We\'ll evaluate your furnace and give you straightforward recommendations without pressure sales tactics.' }
    ]
  },
  'backflow-testing-calgary': {
    title: 'Backflow Testing in Calgary: What Homeowners Need to Know',
    excerpt: 'Understanding City of Calgary backflow testing requirements and how to stay compliant.',
    image: '/images/dnb/blog-backflow.jpg',
    imageAlt: 'Certified backflow testing on a Calgary property by DNB',
    category: 'Plumbing',
    readTime: '5 min read',
    date: 'January 3, 2026',
    content: [
      { type: 'h2', text: 'Protecting Calgary\'s Water Supply' },
      { type: 'p', text: 'Backflow preventers are critical safety devices that protect Calgary\'s drinking water supply from contamination. If your property has a backflow preventer, the City of Calgary requires annual testing to ensure it\'s working properly.' },
      { type: 'h3', text: 'What is Backflow?' },
      { type: 'p', text: 'Backflow occurs when water flows backward through the plumbing system, potentially contaminating the municipal water supply. This can happen due to pressure changes in the water system.' },
      { type: 'h3', text: 'Who Needs Backflow Testing?' },
      { type: 'list', items: ['Properties with irrigation systems', 'Buildings with fire suppression systems', 'Commercial properties', 'Properties with boiler systems', 'Any property with a backflow preventer installed'] },
      { type: 'h3', text: 'Testing Requirements' },
      { type: 'p', text: 'The City of Calgary requires annual testing by a certified tester. Test results must be submitted to the City to maintain compliance. Failure to test can result in fines.' },
      { type: 'p', text: 'DNB provides City of Calgary certified backflow testing. Contact us to schedule your annual test.' }
    ]
  },
  'preventing-frozen-pipes-calgary': {
    title: 'Preventing Frozen Pipes in Calgary Winters',
    excerpt: 'Essential tips to protect your plumbing from Calgary\'s freezing temperatures.',
    image: '/images/dnb/blog-frozen-pipes.jpg',
    imageAlt: 'Calgary winter pipe protection and frozen pipe prevention by DNB',
    category: 'Plumbing',
    readTime: '4 min read',
    date: 'December 28, 2025',
    content: [
      { type: 'h2', text: 'Protecting Your Plumbing in Extreme Cold' },
      { type: 'p', text: 'Calgary\'s winters regularly bring temperatures below -20°C, creating real risks for your home\'s plumbing. Frozen pipes can burst, causing thousands of dollars in water damage.' },
      { type: 'h3', text: 'Prevention Tips' },
      { type: 'list', items: ['Keep indoor temperature at least 15°C, even when away', 'Open cabinet doors under sinks on exterior walls', 'Let faucets drip slightly during extreme cold', 'Insulate pipes in unheated areas', 'Disconnect and drain outdoor hoses before winter', 'Know where your main water shut-off is located'] },
      { type: 'h3', text: 'If Pipes Freeze' },
      { type: 'p', text: 'If you turn on a faucet and nothing comes out during cold weather, you may have frozen pipes. Never use open flame to thaw pipes. Call a professional for assistance.' },
      { type: 'p', text: 'DNB provides fast urgent service for frozen and burst pipes. Contact us immediately if you experience a plumbing emergency.' }
    ]
  },
  'water-heater-maintenance-tips': {
    title: 'Water Heater Care Tips to Extend Equipment Life',
    excerpt: 'Simple care tasks that can extend your water heater\'s life and improve efficiency.',
    image: '/images/dnb/blog-water-heater-maintenance.jpg',
    imageAlt: 'Calgary water heater service and inspection by DNB',
    category: 'Plumbing',
    readTime: '4 min read',
    date: 'December 25, 2025',
    content: [
      { type: 'h2', text: 'Keep Your Water Heater Running Efficiently' },
      { type: 'p', text: 'Your water heater works hard every day. With some basic care, you can extend its lifespan and keep it running efficiently.' },
      { type: 'h3', text: 'Care Tasks' },
      { type: 'list', items: ['Check the temperature setting (120°F/49°C is ideal)', 'Test the pressure relief valve annually', 'Drain a few gallons annually to remove sediment', 'Check for signs of corrosion or leaks', 'Inspect the anode rod every few years'] },
      { type: 'h3', text: 'Signs You Need Professional Service' },
      { type: 'list', items: ['Rusty water from hot water taps', 'Rumbling or popping noises', 'Water around the base of the unit', 'Inconsistent water temperature', 'Reduced hot water supply'] },
      { type: 'p', text: 'Contact DNB for professional water heater service.' }
    ]
  },
  'bbq-gas-line-installation': {
    title: 'BBQ Gas Line Installation: What Calgary Homeowners Should Know',
    excerpt: 'Thinking about a natural gas BBQ line? Here\'s what\'s involved and why it\'s worth it.',
    image: '/images/dnb/blog-bbq-gas.jpg',
    imageAlt: 'Permanent natural gas BBQ line installed by DNB in Calgary',
    category: 'Gas Fitting',
    readTime: '4 min read',
    date: 'December 20, 2025',
    content: [
      { type: 'h2', text: 'Say Goodbye to Propane Tanks' },
      { type: 'p', text: 'A natural gas BBQ line is one of the best outdoor upgrades for Calgary homeowners. No more propane runs, no running out mid-cookout, and lower fuel costs.' },
      { type: 'h3', text: 'Benefits' },
      { type: 'list', items: ['Unlimited fuel supply', 'Lower cost per BTU than propane', 'No tank storage or refilling', 'Cleaner burning fuel', 'Always ready to use'] },
      { type: 'h3', text: 'Installation Process' },
      { type: 'p', text: 'A professional installation includes running a gas line from your home\'s gas supply to your BBQ location, installing a shut-off valve and quick-connect fitting, and obtaining required permits.' },
      { type: 'h3', text: 'Cost' },
      { type: 'p', text: 'BBQ gas line installation typically costs $300-$800 depending on distance and routing. The investment pays off quickly in convenience and fuel savings.' },
      { type: 'p', text: 'Contact DNB for a quote on BBQ gas line installation.' }
    ]
  },
  'hydronic-heating-calgary': {
    title: 'Hydronic Heating Systems: A Guide for Calgary Homeowners',
    excerpt: 'Understanding boiler and hydronic heating systems common in Calgary homes.',
    image: '/images/dnb/blog-hydronic.jpg',
    imageAlt: 'Calgary hydronic boiler and radiant heating system serviced by DNB',
    category: 'Heating',
    readTime: '6 min read',
    date: 'December 15, 2025',
    content: [
      { type: 'h2', text: 'Understanding Your Boiler Heating System' },
      { type: 'p', text: 'Many Calgary homes, especially older homes and townhouses, have hydronic (boiler) heating systems. These systems use hot water circulating through radiators or baseboard heaters to warm your home.' },
      { type: 'h3', text: 'How Hydronic Heating Works' },
      { type: 'p', text: 'A boiler heats water, which is then circulated through your home via pipes to radiators, baseboard heaters, or in-floor tubing. The water releases its heat and returns to the boiler to be reheated.' },
      { type: 'h3', text: 'Common Components' },
      { type: 'list', items: ['Boiler - heats the water', 'Circulator pump - moves water through the system', 'Expansion tank - handles pressure changes', 'Zone valves - control heating to different areas', 'Radiators or baseboard heaters - release heat'] },
      { type: 'h3', text: 'Common Issues' },
      { type: 'list', items: ['Air in the system (requires purging)', 'Circulator pump failure', 'Zone valve problems', 'Pressure issues', 'Boiler service needs'] },
      { type: 'p', text: 'DNB specializes in hydronic heating service for Calgary homes. Contact us for boiler service and repairs.' }
    ]
  }
};

export const BlogPostPage = () => {
  const { postId } = useParams();
  const post = blogPosts[postId];

  if (!post) {
    return (
      <main className="min-h-screen flex items-center justify-center section-tint">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-[#0F172A] mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#0EA5E9] hover:underline">
            Back to Resources
          </Link>
        </div>
      </main>
    );
  }

  const renderContent = (item, index) => {
    switch (item.type) {
      case 'h2':
        return <h2 key={index} className="font-heading font-bold text-2xl text-[#0F172A] mt-8 mb-4">{item.text}</h2>;
      case 'h3':
        return <h3 key={index} className="font-heading font-bold text-xl text-[#0F172A] mt-6 mb-3">{item.text}</h3>;
      case 'h4':
        return <h4 key={index} className="font-heading font-semibold text-lg text-[#0F172A] mt-4 mb-2">{item.text}</h4>;
      case 'p':
        return <p key={index} className="text-slate-600 mb-4 leading-relaxed">{item.text}</p>;
      case 'list':
        return (
          <ul key={index} className="list-disc list-inside space-y-2 mb-4 text-slate-600 ml-4">
            {item.items.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
        );
      case 'alert':
        return (
          <div key={index} className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 font-medium">{item.text}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <main data-testid={`blog-post-${postId}`} className="page-transition">
      {/* Hero */}
      <section className="pt-32 pb-8 section-tint">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-500 hover:text-[#0F172A] mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="px-3 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-full text-sm font-medium">
              {post.category}
            </span>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-4 mb-6">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-8 section-tint">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <img 
            src={post.image} 
            alt={post.imageAlt || post.title}
            className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            {post.content.map((item, index) => renderContent(item, index))}
          </article>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-br from-[#111827] to-[#0F172A] rounded-2xl text-white text-center">
            <h3 className="font-heading font-bold text-2xl mb-4">Need Professional Help?</h3>
            <p className="text-white/80 mb-6">
              Our certified technicians are here to help with all your plumbing, heating, and gas fitting needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-[#0F172A] font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:${PHONE}`}
                className="border-2 border-white text-white font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                {PHONE}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-12 section-tint">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-6">More Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(blogPosts)
              .filter(([id]) => id !== postId)
              .slice(0, 2)
              .map(([id, relatedPost]) => (
                <Link
                  key={id}
                  to={`/blog/${id}`}
                  className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-[#0EA5E9] transition-colors group"
                >
                  <img 
                    src={relatedPost.image} 
                    alt={relatedPost.imageAlt || relatedPost.title}
                    className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                  />
                  <div>
                    <span className="text-xs text-[#0EA5E9] font-medium">{relatedPost.category}</span>
                    <h4 className="font-heading font-bold text-[#0F172A] group-hover:text-[#0F172A] transition-colors line-clamp-2 mt-1">
                      {relatedPost.title}
                    </h4>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPostPage;
